import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ShoppingBag,
  Plus,
  Minus,
  X,
  MapPin,
  Clock,
  Loader2,
  CheckCircle2,
  Phone,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import { deliveryApi } from "../services/deliveryApi";
import { resolveBackendImage } from "../utils/brandLogo";
import ModalComplementos from "../components/delivery/ModalComplementos";

const formatBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function DeliveryCardapioPublico() {
  const { municipioSlug, negocioSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Carrinho local: { [cart_key]: { produto, quantidade, observacao, complementos_escolhidos?, observacao_item?, _grupos_nomes?, _opcoes_nomes? } }
  // cart_key = produto.id (sem complementos) OU produto.id::sorted(opcao_ids)
  const [carrinho, setCarrinho] = useState({});
  const [showCarrinho, setShowCarrinho] = useState(false);

  // Modal de complementos (cardápio público)
  const [produtoComComplementos, setProdutoComComplementos] = useState(null);

  // Checkout
  const [tipoEntrega, setTipoEntrega] = useState("entrega"); // entrega | retirada
  const [zonaId, setZonaId] = useState("");
  const [cliente, setCliente] = useState({ nome: "", telefone: "", email: "" });
  const [endereco, setEndereco] = useState({ logradouro: "", numero: "", bairro: "", complemento: "", referencia: "" });
  const [formaPagamento, setFormaPagamento] = useState("dinheiro");
  const [trocoPara, setTrocoPara] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmedPedido, setConfirmedPedido] = useState(null);

  // Cupom
  const [cupomInput, setCupomInput] = useState("");
  const [cupomAplicado, setCupomAplicado] = useState(null);
  const [validandoCupom, setValidandoCupom] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Delivery desativado neste município? (painel Delivery — Municípios)
        try {
          const mfRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/v2/municipio-features/public?municipio_slug=${municipioSlug}`
          );
          const mf = await mfRes.json();
          if (mf && mf.delivery_enabled === false) {
            setErr("O sistema de delivery está temporariamente indisponível neste município.");
            setLoading(false);
            return;
          }
        } catch {
          // falha na checagem não bloqueia o cardápio
        }
        const r = await deliveryApi.getPublicCardapio(municipioSlug, negocioSlug);
        setData(r);
        // valor default da zona
        if (r.zonas?.length === 1) setZonaId(r.zonas[0].id);

        // Reorder: lê itens da última sessão (se houver) e pré-popula carrinho
        try {
          const negocioId = r?.negocio?.id;
          const key = negocioId ? `dlv-reorder-${negocioId}` : null;
          const raw = key ? sessionStorage.getItem(key) : null;
          if (raw && r.produtos?.length) {
            const parsed = JSON.parse(raw);
            const expired = Date.now() - (parsed.ts || 0) > 5 * 60 * 1000; // 5min
            if (!expired && Array.isArray(parsed.itens)) {
              const indice = {};
              r.produtos.forEach((p) => (indice[p.id] = p));
              const novo = {};
              parsed.itens.forEach((it) => {
                const prod = indice[it.produto_id];
                if (prod && prod.disponivel !== false) {
                  novo[prod.id] = {
                    cart_key: prod.id,
                    produto: prod,
                    quantidade: it.quantidade || 1,
                    observacao: "",
                  };
                }
              });
              if (Object.keys(novo).length) {
                setCarrinho(novo);
                setShowCarrinho(true);
                toast.success(
                  `Itens do pedido ${parsed.ref_pedido} adicionados ao carrinho 🛒`
                );
              } else {
                toast.info(
                  "Alguns itens do seu pedido anterior não estão mais disponíveis."
                );
              }
            }
            // Sempre limpa a chave após uma leitura válida — evita órfãos
            // mesmo quando nenhum produto do reorder existe mais.
            sessionStorage.removeItem(key);
          }
        } catch {
          // sessionStorage indisponível ou JSON inválido — silencia
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || "Delivery indisponível";
        setErr(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [municipioSlug, negocioSlug]);

  // Helpers de carrinho
  const addItem = useCallback((produto) => {
    // Apenas para produtos SEM complementos — chave = produto.id
    setCarrinho((prev) => {
      const cur = prev[produto.id];
      const novo = { ...prev };
      novo[produto.id] = {
        cart_key: produto.id,
        produto,
        quantidade: (cur?.quantidade || 0) + 1,
        observacao: cur?.observacao || "",
      };
      return novo;
    });
  }, []);

  const removeItem = useCallback((cartKey) => {
    setCarrinho((prev) => {
      const cur = prev[cartKey];
      const novo = { ...prev };
      if (!cur) return prev;
      if (cur.quantidade <= 1) delete novo[cartKey];
      else novo[cartKey] = { ...cur, quantidade: cur.quantidade - 1 };
      return novo;
    });
  }, []);

  const incItem = useCallback((cartKey) => {
    setCarrinho((prev) => {
      const cur = prev[cartKey];
      if (!cur) return prev;
      return { ...prev, [cartKey]: { ...cur, quantidade: cur.quantidade + 1 } };
    });
  }, []);

  // Adicionar produto COM complementos — cart_key inclui as escolhas
  const handleAddComComplementos = useCallback(({ produto, quantidade, complementos_escolhidos, observacao_item }) => {
    const escolhaIds = (complementos_escolhidos || [])
      .map((c) => c.opcao_id)
      .slice()
      .sort()
      .join(",");
    const cart_key = `${produto.id}::${escolhaIds}`;
    const grupos_nomes = {};
    const opcoes_nomes = {};
    (complementos_escolhidos || []).forEach((c) => {
      if (c._grupo_nome) grupos_nomes[c.grupo_id] = c._grupo_nome;
      if (c._opcao_nome) opcoes_nomes[c.opcao_id] = c._opcao_nome;
    });
    const precoExtra = (complementos_escolhidos || []).reduce(
      (sum, c) => sum + (c._preco_adicional || 0),
      0
    );
    setCarrinho((prev) => {
      const cur = prev[cart_key];
      const novo = { ...prev };
      novo[cart_key] = {
        cart_key,
        produto,
        quantidade: (cur?.quantidade || 0) + quantidade,
        observacao: "",
        complementos_escolhidos: complementos_escolhidos.map((c) => ({
          grupo_id: c.grupo_id, opcao_id: c.opcao_id,
        })),
        observacao_item: observacao_item || "",
        _grupos_nomes: grupos_nomes,
        _opcoes_nomes: opcoes_nomes,
        _preco_extra: precoExtra,
      };
      return novo;
    });
    setProdutoComComplementos(null);
    toast.success(`${produto.nome} adicionado`);
  }, []);

  const totalItens = useMemo(
    () => Object.values(carrinho).reduce((a, b) => a + b.quantidade, 0),
    [carrinho]
  );
  const subtotal = useMemo(
    () =>
      Object.values(carrinho).reduce(
        (a, b) => a + b.quantidade * ((b.produto.preco || 0) + (b._preco_extra || 0)),
        0
      ),
    [carrinho]
  );

  const zonaSelecionada = useMemo(
    () => data?.zonas?.find((z) => z.id === zonaId),
    [data, zonaId]
  );
  const taxa = tipoEntrega === "entrega" ? zonaSelecionada?.taxa_entrega || 0 : 0;
  const desconto = cupomAplicado?.desconto || 0;
  const total = Math.max(0, subtotal + taxa - desconto);

  const handleAplicarCupom = async () => {
    const codigo = (cupomInput || "").trim();
    if (!codigo || !data?.loja?.id) return;
    setValidandoCupom(true);
    try {
      const r = await deliveryApi.validarCupomPublico(data.loja.id, codigo, subtotal);
      setCupomAplicado(r);
      toast.success(`Cupom aplicado: -${new Intl.NumberFormat("pt-BR", {style:"currency",currency:"BRL"}).format(r.desconto)}`);
    } catch (e) {
      setCupomAplicado(null);
      toast.error(e?.response?.data?.detail || "Cupom inválido");
    } finally {
      setValidandoCupom(false);
    }
  };

  // Re-valida cupom quando subtotal muda (carrinho alterado)
  useEffect(() => {
    if (!cupomAplicado || !data?.loja?.id) return;
    // Apenas re-aplica silenciosamente para atualizar desconto
    (async () => {
      try {
        const r = await deliveryApi.validarCupomPublico(data.loja.id, cupomAplicado.codigo, subtotal);
        setCupomAplicado(r);
      } catch {
        // Se ficou abaixo do mínimo, remove o cupom
        setCupomAplicado(null);
        toast.info("Cupom removido — subtotal abaixo do mínimo");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const podeFinalizar =
    totalItens > 0 &&
    cliente.nome.trim() &&
    cliente.telefone.trim() &&
    (tipoEntrega === "retirada" || (zonaId && endereco.logradouro.trim()));

  const handleFinalizar = async () => {
    if (!podeFinalizar) return;
    setSubmitting(true);
    try {
      const itens = Object.values(carrinho).map((c) => ({
        produto_id: c.produto.id,
        quantidade: c.quantidade,
        observacao: c.observacao || null,
        complementos_escolhidos: c.complementos_escolhidos || [],
        observacao_item: c.observacao_item || null,
      }));
      const payload = {
        loja_id: data.loja.id,
        cliente: {
          nome: cliente.nome.trim(),
          telefone: cliente.telefone.trim(),
          email: cliente.email.trim() || null,
          endereco_entrega: tipoEntrega === "entrega" ? endereco : null,
        },
        itens,
        forma_pagamento: formaPagamento,
        tipo: tipoEntrega,
        zona_id: tipoEntrega === "entrega" ? zonaId : null,
        observacoes: observacoes || null,
        troco_para: formaPagamento === "dinheiro" && trocoPara ? parseFloat(trocoPara) : null,
        cupom_codigo: cupomAplicado?.codigo || null,
      };
      const pedido = await deliveryApi.createPedido(payload);
      setConfirmedPedido(pedido);
      setCarrinho({});
      setShowCarrinho(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
          <h2 className="text-lg font-semibold mb-2">{err || "Indisponível"}</h2>
          <Link to="/" className="text-sm text-[var(--primary)] underline">
            Voltar para o início
          </Link>
        </div>
      </div>
    );
  }

  const { loja, negocio, produtos, zonas } = data;
  const lojaInativa = !loja.ativo;

  // ===== Tela de confirmação =====
  if (confirmedPedido) {
    // Monta uma mensagem rica de WhatsApp com itens e total — assim o lojista
    // recebe todos os detalhes mesmo se a notificação push falhar.
    const itensConfirmados = confirmedPedido.itens || [];
    const linhasItens = itensConfirmados
      .map((it) => `• ${it.quantidade}× ${it.nome} — ${formatBRL(it.subtotal)}`)
      .join("\n");
    const enderecoStr = confirmedPedido.tipo === "entrega"
      ? `Entrega: ${[
          confirmedPedido.cliente?.endereco_entrega?.logradouro,
          confirmedPedido.cliente?.endereco_entrega?.numero,
          confirmedPedido.cliente?.endereco_entrega?.bairro,
        ].filter(Boolean).join(", ")}`
      : "Retirada na loja";
    const mensagemWa = `Olá! Acabei de enviar o pedido *${confirmedPedido.numero_curto}* pelo cardápio digital.

*Itens*:
${linhasItens}

*Total*: ${formatBRL(confirmedPedido.total)}
*Pagamento*: ${confirmedPedido.forma_pagamento}
*${enderecoStr}*

Pode confirmar?`;
    const whatsappHref = `https://wa.me/55${(loja?.telefone || negocio?.telefone || "").replace(/\D/g, "")}?text=${encodeURIComponent(mensagemWa)}`;
    const whatsappDisponivel = !!(loja?.telefone || negocio?.telefone);

    return (
      <div className="min-h-screen bg-[var(--bg-secondary)] py-10 px-4">
        <div className="max-w-md mx-auto bg-white rounded-2xl border border-[var(--border)] p-6 text-center shadow-sm">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-3" />
          <h2 className="text-xl font-bold mb-2" data-testid="delivery-pedido-confirmado">
            Pedido enviado!
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            O lojista foi notificado no painel. Para garantir uma resposta rápida,
            envie também uma mensagem direta no WhatsApp abaixo.
          </p>
          <div className="text-2xl font-mono font-bold text-[var(--primary)] mb-3">
            {confirmedPedido.numero_curto}
          </div>
          <p className="text-sm">
            Total: <strong>{formatBRL(confirmedPedido.total)}</strong>
          </p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            Pagamento: {confirmedPedido.forma_pagamento}
          </p>

          {whatsappDisponivel ? (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mt-5 px-4 py-3 text-sm font-semibold bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 shadow-md"
              data-testid="delivery-whatsapp-confirm"
            >
              <Phone className="w-4 h-4" /> Enviar pedido pelo WhatsApp
              <span className="text-[10px] opacity-90 font-normal ml-1">recomendado</span>
            </a>
          ) : (
            <p className="text-xs text-amber-700 mt-5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              A loja ainda não cadastrou WhatsApp. Acompanhe pelo link abaixo.
            </p>
          )}

          <Link
            to={`/delivery/p/${String(confirmedPedido.numero_curto).replace(/^#/, "")}`}
            className="block w-full text-center mt-3 px-4 py-2 text-sm bg-[var(--primary)] text-white rounded-lg hover:opacity-90"
            data-testid="delivery-track-link"
          >
            Acompanhar pedido em tempo real →
          </Link>

          <button
            onClick={() => setConfirmedPedido(null)}
            className="block mx-auto mt-4 text-xs text-[var(--text-secondary)] underline"
            data-testid="delivery-novo-pedido"
          >
            Fazer outro pedido
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)] pb-24">
      {/* Hero */}
      <div className="bg-gradient-to-br from-orange-500 to-red-500 text-white">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <Link
            to={`/${municipioSlug}`}
            className="inline-flex items-center gap-2 text-xs opacity-90 hover:opacity-100"
            data-testid="delivery-public-back"
          >
            <ArrowLeft className="w-3 h-3" /> {data.municipio?.nome}
          </Link>
          <h1 className="text-2xl font-bold mt-2" data-testid="delivery-loja-nome">
            {loja.nome_loja}
          </h1>
          {loja.descricao && (
            <p className="text-sm opacity-90 mt-1">{loja.descricao}</p>
          )}
          <div className="flex flex-wrap gap-3 mt-3 text-xs opacity-90">
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> ~{loja.tempo_medio_preparo_min}min
            </span>
            {zonas?.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {zonas.length} zona(s) de entrega
              </span>
            )}
          </div>
        </div>
      </div>

      {lojaInativa && (
        <div className="max-w-3xl mx-auto px-4 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Loja temporariamente pausada — pedidos indisponíveis no momento.
          </div>
        </div>
      )}

      {/* Lista de produtos agrupados por categoria */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {produtos.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-[var(--border)] p-8 text-center text-sm text-[var(--text-secondary)]">
            Sem produtos disponíveis no momento.
          </div>
        ) : (
          (() => {
            const categorias = data.cardapio?.categorias || [];
            const byId = {};
            (categorias || []).forEach((c) => (byId[c.id] = c));
            const groups = {};
            produtos.forEach((p) => {
              const key = p.categoria_id && byId[p.categoria_id] ? p.categoria_id : "__sem__";
              if (!groups[key]) groups[key] = [];
              groups[key].push(p);
            });
            const ordered = categorias
              .slice()
              .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
              .filter((c) => groups[c.id]?.length);
            if (groups["__sem__"]?.length) ordered.push({ id: "__sem__", nome: ordered.length ? "Outros" : "Cardápio" });

            return ordered.map((cat) => (
              <section key={cat.id} className="mb-6" data-testid={`delivery-public-cat-${cat.id}`}>
                <h2 className="text-base font-semibold mb-2 text-[var(--text-primary)] flex items-center gap-2">
                  <span className="inline-block w-1 h-4 bg-[var(--primary)] rounded" />
                  {cat.nome}
                  <span className="text-xs text-[var(--text-secondary)] font-normal">
                    ({groups[cat.id].length})
                  </span>
                </h2>
                <div className="space-y-2">
                  {groups[cat.id].map((p) => {
                    // Qtd no carrinho = soma de todas as variantes (com/sem complementos)
                    const qtd = Object.values(carrinho)
                      .filter((c) => c.produto.id === p.id)
                      .reduce((a, b) => a + b.quantidade, 0);
                    const temComplementos = (p.grupos_complementos || []).length > 0;
                    const unidade = p.unidade_medida && p.unidade_medida !== "unidade"
                      ? p.unidade_medida : null;
                    const estLimitado = p.estoque?.tipo === "limitado";
                    const estQty = p.estoque?.quantidade ?? 0;
                    const esgotado = estLimitado && estQty <= 0;
                    const ultimasUnidades = estLimitado && estQty > 0 && estQty <= 5;
                    const foraDoTurno = p.disponivel_agora === false;
                    const indisponivel = esgotado || foraDoTurno;
                    const restricoes = p.restricoes_alimentares || [];
                    return (
                      <div
                        key={p.id}
                        className={`bg-white rounded-xl border border-[var(--border)] p-3 flex items-start gap-3 ${
                          indisponivel ? "opacity-60" : ""
                        }`}
                        data-testid={`delivery-public-produto-${p.id}`}
                      >
                        {p.foto_url && (
                          <img
                            src={resolveBackendImage(p.foto_url)}
                            alt={p.nome}
                            className="w-20 h-20 rounded-lg object-cover bg-[var(--bg-secondary)] shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold flex items-center gap-1.5 flex-wrap">
                            {p.nome}
                            {p.tipo_produto === "organico" && (
                              <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">🌱 Orgânico</span>
                            )}
                            {p.tipo_produto === "agroecologico" && (
                              <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">🌿 Agroecológico</span>
                            )}
                            {p.tipo_produto === "artesanal" && (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">🤲 Artesanal</span>
                            )}
                          </h3>
                          {p.descricao && (
                            <p className="text-xs text-[var(--text-secondary)] mt-0.5 line-clamp-2">
                              {p.descricao}
                            </p>
                          )}
                          {restricoes.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {restricoes
                                .filter((r) => !["organico", "agroecologico"].includes(r))
                                .map((r) => (
                                  <span
                                    key={r}
                                    className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-full"
                                  >
                                    {r === "vegano" && "🌱 Vegano"}
                                    {r === "vegetariano" && "🥗 Vegetariano"}
                                    {r === "sem_gluten" && "🌾 Sem glúten"}
                                    {r === "sem_lactose" && "🥛 Sem lactose"}
                                  </span>
                                ))}
                            </div>
                          )}
                          <p className="text-base font-bold text-[var(--primary)] mt-1">
                            {formatBRL(p.preco)}
                            {unidade && (
                              <span className="text-xs text-[var(--text-secondary)] font-normal"> / {unidade}</span>
                            )}
                          </p>
                          {/* Badges de estado */}
                          <div className="flex flex-wrap gap-1 mt-1">
                            {esgotado && (
                              <span className="text-[10px] bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full font-semibold">
                                Esgotado
                              </span>
                            )}
                            {ultimasUnidades && (
                              <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-semibold">
                                ⚠️ Últimas {estQty} unidades
                              </span>
                            )}
                            {foraDoTurno && (
                              <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full">
                                Disponível em outro horário
                              </span>
                            )}
                            {(p.tipo_disponibilidade === "encomenda" || p.tipo_disponibilidade === "ambos") && (
                              <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
                                📅 Encomenda
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {qtd > 0 && !temComplementos && (
                            <>
                              <button
                                onClick={() => removeItem(p.id)}
                                className="p-1.5 border border-[var(--border)] rounded-lg hover:bg-[var(--bg-secondary)]"
                                data-testid={`delivery-public-minus-${p.id}`}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span
                                className="font-semibold w-6 text-center"
                                data-testid={`delivery-public-qty-${p.id}`}
                              >
                                {qtd}
                              </span>
                            </>
                          )}
                          {qtd > 0 && temComplementos && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full font-semibold"
                              data-testid={`delivery-public-qty-${p.id}`}
                            >
                              {qtd} no carrinho
                            </span>
                          )}
                          <button
                            onClick={() => temComplementos ? setProdutoComComplementos(p) : addItem(p)}
                            disabled={lojaInativa || indisponivel}
                            className="p-1.5 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-30"
                            data-testid={`delivery-public-plus-${p.id}`}
                            title={temComplementos ? "Personalizar produto" : "Adicionar"}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ));
          })()
        )}
      </div>

      {/* Carrinho fixo (FAB) */}
      {totalItens > 0 && !showCarrinho && (
        <button
          onClick={() => setShowCarrinho(true)}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 px-5 py-3 bg-[var(--primary)] text-white rounded-full shadow-2xl flex items-center gap-3 hover:opacity-90 z-30"
          data-testid="delivery-open-cart"
        >
          <ShoppingBag className="w-5 h-5" />
          <span className="font-semibold">{totalItens}</span>
          <span>{formatBRL(subtotal)}</span>
          <span className="text-xs opacity-80">Ver carrinho →</span>
        </button>
      )}

      {/* Drawer do carrinho */}
      {showCarrinho && (
        <div className="fixed inset-0 bg-black/40 z-40 flex justify-end" data-testid="delivery-cart-drawer">
          <div className="bg-white w-full max-w-md h-full overflow-auto">
            <div className="sticky top-0 bg-white border-b border-[var(--border)] p-4 flex items-center justify-between">
              <h2 className="font-bold">Seu pedido</h2>
              <button
                onClick={() => setShowCarrinho(false)}
                className="p-1 hover:bg-[var(--bg-secondary)] rounded"
                data-testid="delivery-cart-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* Itens */}
              {Object.values(carrinho).map((item) => {
                const linhaPrecoUnit = (item.produto.preco || 0) + (item._preco_extra || 0);
                const grupos = Object.entries(item._grupos_nomes || {});
                return (
                  <div
                    key={item.cart_key}
                    className="border-b border-[var(--border)] pb-2"
                    data-testid={`cart-item-${item.cart_key}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.produto.nome}</p>
                        <p className="text-xs text-[var(--text-secondary)]">
                          {formatBRL(linhaPrecoUnit)} ×
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => removeItem(item.cart_key)}
                          className="p-1 border border-[var(--border)] rounded"
                          data-testid={`cart-minus-${item.cart_key}`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="font-semibold w-6 text-center">{item.quantidade}</span>
                        <button
                          onClick={() => incItem(item.cart_key)}
                          className="p-1 bg-[var(--primary)] text-white rounded"
                          data-testid={`cart-plus-${item.cart_key}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    {grupos.length > 0 && (
                      <div className="mt-1 pl-2 border-l-2 border-orange-200 text-[11px] text-[var(--text-secondary)]">
                        {(item.complementos_escolhidos || []).map((c, i) => (
                          <div key={i}>
                            <span className="font-medium">
                              {item._grupos_nomes?.[c.grupo_id] || ""}:
                            </span>{" "}
                            {item._opcoes_nomes?.[c.opcao_id] || ""}
                          </div>
                        ))}
                      </div>
                    )}
                    {item.observacao_item && (
                      <p className="mt-1 text-[11px] italic text-[var(--text-secondary)]">
                        Obs: {item.observacao_item}
                      </p>
                    )}
                  </div>
                );
              })}

              {/* Tipo de entrega */}
              <div>
                <label className="block text-xs font-medium mb-1">Como prefere receber?</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTipoEntrega("entrega")}
                    disabled={!loja.aceita_entrega}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                      tipoEntrega === "entrega"
                        ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                        : "border-[var(--border)]"
                    } disabled:opacity-40`}
                    data-testid="delivery-tipo-entrega"
                  >
                    Entrega
                  </button>
                  <button
                    onClick={() => setTipoEntrega("retirada")}
                    disabled={!loja.aceita_retirada}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                      tipoEntrega === "retirada"
                        ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                        : "border-[var(--border)]"
                    } disabled:opacity-40`}
                    data-testid="delivery-tipo-retirada"
                  >
                    Retirar na loja
                  </button>
                </div>
              </div>

              {/* Cliente */}
              <div>
                <label className="block text-xs font-medium mb-1">Seu nome *</label>
                <input
                  type="text"
                  value={cliente.nome}
                  onChange={(e) => setCliente({ ...cliente, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                  data-testid="delivery-cliente-nome"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Telefone (com DDD) *</label>
                <input
                  type="tel"
                  value={cliente.telefone}
                  onChange={(e) => setCliente({ ...cliente, telefone: e.target.value })}
                  placeholder="(31) 99999-0000"
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                  data-testid="delivery-cliente-telefone"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">
                  E-mail <span className="text-[var(--text-secondary)]">(opcional, para receber atualizações)</span>
                </label>
                <input
                  type="email"
                  value={cliente.email}
                  onChange={(e) => setCliente({ ...cliente, email: e.target.value })}
                  placeholder="seu@email.com"
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                  data-testid="delivery-cliente-email"
                />
              </div>

              {/* Endereço (só entrega) */}
              {tipoEntrega === "entrega" && (
                <>
                  <div>
                    <label className="block text-xs font-medium mb-1">Zona de entrega *</label>
                    <select
                      value={zonaId}
                      onChange={(e) => setZonaId(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                      data-testid="delivery-zona-select"
                    >
                      <option value="">Selecione…</option>
                      {zonas.map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.nome} — {formatBRL(z.taxa_entrega)} (~{z.tempo_estimado_min}min)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Logradouro *</label>
                    <input
                      type="text"
                      value={endereco.logradouro}
                      onChange={(e) => setEndereco({ ...endereco, logradouro: e.target.value })}
                      placeholder="Rua / Av."
                      className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                      data-testid="delivery-endereco-logradouro"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium mb-1">Número</label>
                      <input
                        type="text"
                        value={endereco.numero}
                        onChange={(e) => setEndereco({ ...endereco, numero: e.target.value })}
                        className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                        data-testid="delivery-endereco-numero"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Bairro</label>
                      <input
                        type="text"
                        value={endereco.bairro}
                        onChange={(e) => setEndereco({ ...endereco, bairro: e.target.value })}
                        className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                        data-testid="delivery-endereco-bairro"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Complemento / referência</label>
                    <input
                      type="text"
                      value={endereco.referencia}
                      onChange={(e) => setEndereco({ ...endereco, referencia: e.target.value })}
                      className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                      data-testid="delivery-endereco-ref"
                    />
                  </div>
                </>
              )}

              {/* Pagamento */}
              <div>
                <label className="block text-xs font-medium mb-1">Forma de pagamento</label>
                <select
                  value={formaPagamento}
                  onChange={(e) => setFormaPagamento(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                  data-testid="delivery-pagamento"
                >
                  <option value="dinheiro">Dinheiro</option>
                  <option value="pix">PIX</option>
                  <option value="cartao_entrega">Cartão na entrega</option>
                </select>
              </div>
              {formaPagamento === "dinheiro" && (
                <div>
                  <label className="block text-xs font-medium mb-1">Troco para (opcional)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={trocoPara}
                    onChange={(e) => setTrocoPara(e.target.value)}
                    className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                    data-testid="delivery-troco"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium mb-1">Observações (opcional)</label>
                <textarea
                  rows={2}
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                  data-testid="delivery-observacoes"
                />
              </div>

              {/* Cupom de desconto */}
              <div>
                <label className="block text-xs font-medium mb-1">Cupom de desconto</label>
                {cupomAplicado ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm">
                    <span className="font-mono font-bold text-emerald-700">
                      {cupomAplicado.codigo}
                    </span>
                    <span className="text-xs text-emerald-700 flex-1">
                      -{formatBRL(cupomAplicado.desconto)} aplicado
                    </span>
                    <button
                      onClick={() => {
                        setCupomAplicado(null);
                        setCupomInput("");
                      }}
                      className="text-xs text-red-600 hover:underline"
                      data-testid="delivery-cupom-remover"
                    >
                      remover
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={cupomInput}
                      onChange={(e) => setCupomInput(e.target.value.toUpperCase())}
                      placeholder="Cole o código aqui"
                      className="flex-1 px-3 py-2 border border-[var(--border)] rounded-lg text-sm font-mono uppercase"
                      data-testid="delivery-cupom-input"
                    />
                    <button
                      onClick={handleAplicarCupom}
                      disabled={!cupomInput.trim() || validandoCupom}
                      className="px-3 py-2 text-sm border border-[var(--primary)] text-[var(--primary)] rounded-lg disabled:opacity-50"
                      data-testid="delivery-cupom-aplicar"
                    >
                      {validandoCupom ? "..." : "Aplicar"}
                    </button>
                  </div>
                )}
              </div>

              {/* Totais */}
              <div className="border-t border-[var(--border)] pt-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>{formatBRL(subtotal)}</span>
                </div>
                {tipoEntrega === "entrega" && (
                  <div className="flex justify-between text-[var(--text-secondary)]">
                    <span>Taxa de entrega</span>
                    <span>{formatBRL(taxa)}</span>
                  </div>
                )}
                {desconto > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Desconto ({cupomAplicado?.codigo})</span>
                    <span>-{formatBRL(desconto)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-1">
                  <span>Total</span>
                  <span data-testid="delivery-total-final">{formatBRL(total)}</span>
                </div>
              </div>

              <button
                onClick={handleFinalizar}
                disabled={!podeFinalizar || submitting}
                className="w-full px-4 py-3 bg-[var(--primary)] text-white rounded-lg font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="delivery-finalizar"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>Finalizar pedido • {formatBRL(total)}</>
                )}
              </button>
              <p className="text-[10px] text-center text-[var(--text-secondary)]">
                Você receberá uma confirmação ao finalizar. O pagamento é combinado
                diretamente com o lojista.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de complementos */}
      {produtoComComplementos && (
        <ModalComplementos
          produto={produtoComComplementos}
          onClose={() => setProdutoComComplementos(null)}
          onAdd={handleAddComComplementos}
        />
      )}
    </div>
  );
}
